/**
 * HQ 점주 가입 승인 — 스스로 가입 신청한 점주를 본부가 확인하고 승인/거부한다.
 *
 * 승인하면 그 계정으로 바로 로그인할 수 있게 되고 점주에게 알림톡이 나간다.
 * 확인 창(window.confirm)은 환경에 따라 차단되므로 인라인 2단계 버튼으로 처리한다.
 * 2026-10-28 대표 지시: 승인 때 구독 요금(정가 50만 기본)에 할인을 적용할 수 있다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import hqSignupService, { HqSignupRegistration } from '../services/hqSignupService';
import HqPriceInput, { HqPriceValue, priceToBody, priceToValue } from '../components/HqPriceInput';

type Tab = 'pending' | 'approved' | 'rejected';

const TAB_LABEL: Record<Tab, string> = {
  pending: '승인 대기',
  approved: '승인 완료',
  rejected: '거부',
};

const fmt = (v: string | null | undefined) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
};

const HqSignupsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('pending');
  const [items, setItems] = useState<HqSignupRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [working, setWorking] = useState(false);
  const [priceInput, setPriceInput] = useState<HqPriceValue>(priceToValue(null));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setConfirmId(null);
    setRejectId(null);
    try {
      setItems(await hqSignupService.list(tab));
    } catch (e: any) {
      setError(e?.response?.data?.detail || '목록을 불러오지 못했습니다.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const doApprove = async (id: string) => {
    const { body, error: priceError } = priceToBody(priceInput);
    if (!body) {
      setError(priceError || '요금을 확인해 주세요');
      return;
    }
    setWorking(true);
    setError(null);
    try {
      await hqSignupService.approve(id, body);
      setNotice(`승인했습니다. 점주가 바로 로그인할 수 있습니다. 구독 요금 월 ${body.monthly_fee.toLocaleString('ko-KR')}원(부가세 별도).`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || '승인에 실패했습니다.');
    } finally {
      setWorking(false);
    }
  };

  const doReject = async (id: string) => {
    if (!rejectReason.trim()) {
      setError('거부 사유를 적어주세요. 점주에게 그대로 전달됩니다.');
      return;
    }
    setWorking(true);
    setError(null);
    try {
      await hqSignupService.reject(id, rejectReason.trim());
      setNotice('거부 처리했습니다.');
      setRejectReason('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || '거부 처리에 실패했습니다.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900">✅ 가입 승인</h1>
        <p className="text-sm text-slate-500 mt-1">
          홈페이지에서 스스로 가입 신청한 점주 목록입니다. 승인해야 로그인할 수 있습니다.
        </p>
      </div>

      <div className="flex gap-2 mb-4">
        {(['pending', 'approved', 'rejected'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {notice && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-center justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-emerald-600">✕</button>
        </div>
      )}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">불러오는 중…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-slate-400 text-sm">
          {tab === 'pending' ? '승인 기다리는 신청이 없습니다.' : '해당하는 내역이 없습니다.'}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div
              key={r.id}
              className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{r.organization_name}</div>
                  <div className="text-sm text-slate-600 mt-1">
                    {r.owner_name} · {r.owner_phone || '-'}
                  </div>
                  <div className="text-sm text-slate-500">{r.owner_email}</div>
                </div>
                <div className="text-xs text-slate-400 whitespace-nowrap">
                  신청 {fmt(r.submitted_at)}
                </div>
              </div>

              <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div className="flex gap-2">
                  <dt className="text-slate-400 w-24 shrink-0">사업자번호</dt>
                  <dd className="text-slate-700">{r.business_registration_number || '-'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-slate-400 w-24 shrink-0">사업장 전화</dt>
                  <dd className="text-slate-700">{r.phone || '-'}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="text-slate-400 w-24 shrink-0">주소</dt>
                  <dd className="text-slate-700">{r.address || '-'}</dd>
                </div>
                {tab === 'approved' && (
                  <div className="flex gap-2 sm:col-span-2">
                    <dt className="text-slate-400 w-24 shrink-0">승인 시각</dt>
                    <dd className="text-slate-700">{fmt(r.approved_at)}</dd>
                  </div>
                )}
                {tab === 'rejected' && (
                  <div className="flex gap-2 sm:col-span-2">
                    <dt className="text-slate-400 w-24 shrink-0">거부 사유</dt>
                    <dd className="text-slate-700">{r.rejection_reason || '-'}</dd>
                  </div>
                )}
              </dl>

              {tab === 'pending' && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  {confirmId === r.id ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="w-full">
                        <p className="text-sm text-slate-600 mb-1">구독 요금 (할인이 없으면 정가 그대로)</p>
                        <HqPriceInput value={priceInput} onChange={setPriceInput} />
                      </div>
                      <span className="text-sm text-slate-700">
                        승인하면 이 점주가 바로 로그인할 수 있게 됩니다. 진행할까요?
                      </span>
                      <button
                        disabled={working}
                        onClick={() => doApprove(r.id)}
                        className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                      >
                        승인 확정
                      </button>
                      <button
                        disabled={working}
                        onClick={() => setConfirmId(null)}
                        className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm"
                      >
                        취소
                      </button>
                    </div>
                  ) : rejectId === r.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={2}
                        placeholder="거부 사유 (점주에게 그대로 전달됩니다)"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                      />
                      <div className="flex gap-2">
                        <button
                          disabled={working}
                          onClick={() => doReject(r.id)}
                          className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
                        >
                          거부 확정
                        </button>
                        <button
                          disabled={working}
                          onClick={() => {
                            setRejectId(null);
                            setRejectReason('');
                          }}
                          className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setConfirmId(r.id);
                          setRejectId(null);
                          setPriceInput(priceToValue(r.price));
                        }}
                        className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
                      >
                        승인
                      </button>
                      <button
                        onClick={() => {
                          setRejectId(r.id);
                          setConfirmId(null);
                          setRejectReason('');
                        }}
                        className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm hover:bg-slate-50"
                      >
                        거부
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HqSignupsPage;
